import moment from "moment";
import _ from "lodash";
import useForceRerender from "../../hooks/useForceRerender";

export const useSalesData = (overviewReport, dateRange) => {
  const allProducts = _.chain(overviewReport.totalSales)
    .map((p) => p.name)
    .keyBy()
    .mapValues(() => 0)
    .value();

  const salesRecords = _.chain(overviewReport.totalSales)
    .map((product) => ({
      sales: product.sales.map((saleInfo) => ({
        ...saleInfo,
        product: { id: product.id, name: product.name }
      }))
    }))
    .map((product) => product.sales)
    .flatten()
    .reduce((i, value) => {
      const dateIndex = i.findIndex((j) => j.date === value.date);

      if (dateIndex >= 0) {
        i[dateIndex][value.product.name] = value.salesCount;
      } else {
        i.push({
          date: moment(new Date(value.date)),
          ...allProducts
        });

        i[i.length - 1][value.product.name] = value.salesCount;
      }

      return i;
    }, [])
    .value();

  const startDate = dateRange.start;
  const endDate = dateRange.end;

  const numberOfDays = endDate.diff(startDate, "days");
  const numberOfMonths =
    endDate.startOf("month").diff(startDate.startOf("month"), "months") + 1;
  let totalSalesGraphData;

  if (numberOfMonths >= 3) {
    const months = Array(numberOfMonths)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "months"))
      .map((date) => ({ date, ...allProducts }));

    salesRecords.forEach((saleRecord) => {
      const matchingDate = months.findIndex((day) =>
        saleRecord.date.isSame(day.date, "month")
      );

      if (months[matchingDate]) {
        Object.keys(saleRecord).map((key) => {
          if (key !== "date" && months[matchingDate][key]) {
            months[matchingDate][key] += saleRecord[key];
          } else if (key !== "date") {
            months[matchingDate][key] = saleRecord[key];
          }
        });
      } else {
        months[matchingDate] = {
          ...saleRecord,
          date: saleRecord.date
        };
      }
    });

    totalSalesGraphData = months.map((m) => ({
      ...m,
      date: m.date.unix() * 1000
    }));
  } else {
    const days = Array(numberOfDays)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "days"))
      .map((date) => ({ date, ...allProducts }));

    salesRecords.forEach((saleRecord) => {
      const matchingDate = days.findIndex((day) =>
        saleRecord.date.isSame(day.date, "day")
      );
      days[matchingDate] = {
        ...saleRecord,
        date: days[matchingDate] ? days[matchingDate].date : saleRecord.date
      };
    });

    totalSalesGraphData = days.map((d) => ({
      ...d,
      date: d.date.unix() * 1000
    }));
  }

  return {
    totalSalesGraphData
  };
};

const useViewsData = (overviewReport, dateRange) => {
  const allVideos = _.chain(overviewReport.totalViews)
    .map((video) => video.name)
    .keyBy()
    .mapValues(() => 0)
    .value();

  const viewRecords = _.chain(overviewReport.totalViews)
    .map((video) => ({
      totalViews: video.totalViews.map((viewInfo) => ({
        ...viewInfo,
        video: { id: video.id, name: video.name }
      }))
    }))
    .map((video) => video.totalViews)
    .flatten()
    .reduce((i, value) => {
      const dateIndex = i.findIndex((j) => j.date === value.date);

      if (dateIndex >= 0) {
        i[dateIndex][value.video.name] = value.viewCount;
      } else {
        i.push({
          date: moment(new Date(value.date)),
          ...allVideos
        });

        i[i.length - 1][value.video.name] = value.viewCount;
      }

      return i;
    }, [])
    .value();

  const startDate = dateRange.start;
  const endDate = dateRange.end;

  const numberOfDays = endDate.diff(startDate, "days");
  const numberOfMonths =
    endDate.startOf("month").diff(startDate.startOf("month"), "months") + 1;
  let totalViewsGraphData;

  if (numberOfMonths >= 3) {
    const months = Array(numberOfMonths)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "months"))
      .map((date) => ({ date, ...allVideos }));

    viewRecords.forEach((viewRecord) => {
      const matchingDate = months.findIndex((day) =>
        viewRecord.date.isSame(day.date, "month")
      );

      if (months[matchingDate]) {
        Object.keys(viewRecord).map((key) => {
          if (key !== "date" && months[matchingDate][key]) {
            months[matchingDate][key] += viewRecord[key];
          } else if (key !== "date") {
            months[matchingDate][key] = viewRecord[key];
          }
        });
      } else {
        months[matchingDate] = {
          ...viewRecord,
          date: viewRecord.date
        };
      }
    });

    totalViewsGraphData = months.map((m) => ({
      ...m,
      date: m.date.unix() * 1000
    }));
  } else {
    const days = Array(numberOfDays)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "days"))
      .map((date) => ({ date, ...allVideos }));

    viewRecords.forEach((viewRecord) => {
      const matchingDate = days.findIndex((day) =>
        viewRecord.date.isSame(day.date, "day")
      );
      days[matchingDate] = {
        ...viewRecord,
        date: days[matchingDate] ? days[matchingDate].date : viewRecord.date
      };
    });

    totalViewsGraphData = days.map((d) => ({
      ...d,
      date: d.date.unix() * 1000
    }));
  }

  return {
    totalViewsGraphData
  };
};

export const useCombineOrigin = (videoOriginList) => {
  if (!videoOriginList.length) return [];
  const otherCount = videoOriginList.reduce((accumulator, element) => {
    if (element.views <= 3 || element.origin === "UNKNOWN") {
      return accumulator + element.views;
    }
    return accumulator;
  }, 0);

  const otherElement = {
    id: "other",
    views: otherCount,
    origin: "Other"
  };

  const filteredVideoOriginListData = videoOriginList.filter(
    (element) => element.views > 3 && element.origin !== "UNKNOWN"
  );
  filteredVideoOriginListData.push(otherElement);

  return filteredVideoOriginListData;
};

const useReportingOverview = (overviewReport, dateRange) => {
  // This is being used here to deal with recharts incorrectly initially rendering
  useForceRerender();

  const { totalViewsGraphData } = useViewsData(overviewReport, {
    start: moment(dateRange[0]),
    end: moment(dateRange[1])
  });

  const { totalSalesGraphData } = useSalesData(overviewReport, {
    start: moment(dateRange[0]),
    end: moment(dateRange[1])
  });

  const productListTableData = overviewReport.productList.map((product) => ({
    id: product.id,
    thumbnail: product.images[0].url,
    name: product.name,
    sku: product.sku,
    videos: product.stats.totalVideosCreated,
    views: product.stats.totalVideoViews,
    sales: product.stats.totalEarnings
  }));

  const videoListTableData = overviewReport.videoList.map((video) => ({
    id: video.id,
    thumbnail: video.thumbnailUrl,
    name: video.name,
    orders: video.stats.totalSales,
    views: video.stats.totalViews,
    sales: video.stats.totalEarnings
  }));

  const videoOriginList = overviewReport.originList.map((origin) => ({
    id: origin.origin,
    origin: origin.origin,
    views: origin.totalViews
  }));

  const videoOriginListTableData = useCombineOrigin(videoOriginList);

  return {
    totalViewsGraphData,
    totalViewsColors: ["#ff7373", "#f5498b", "#ac7df2", "#76aae7", "#494949"],
    totalSalesGraphData,
    totalSalesColors: ["#65cfa1", "#4a90e2", "#ac7df2", "#76aae7", "#494949"],
    videoListTableData,
    productListTableData,
    videoOriginListTableData,
    averageNumberOfViews: overviewReport.averageViews,
    numberOfPinkButtonClicks: overviewReport.pinkButtonClicks,
    numberOfCompletedPurchases: overviewReport.completedPurchases
  };
};

export default useReportingOverview;
